<script setup lang="ts">
import { computed, useSlots } from 'vue';

interface Props {
  type?: 'error' | 'warning' | 'info' | 'success';
  message?: string;
  icon?: string;
  dismissible?: boolean;
  actionButton?: {
    text: string;
    onClick: () => void;
  };
}

const props = withDefaults(defineProps<Props>(), {
  type: 'info',
  message: undefined,
  icon: undefined,
  dismissible: false,
  actionButton: undefined,
});

const emit = defineEmits<{
  dismiss: [];
}>();

const slots = useSlots();

const typeClasses = {
  error: 'bg-error',
  warning: 'bg-orange-500',
  info: 'bg-blue-500',
  success: 'bg-confirm',
};

const iconClasses = {
  error: 'icon-warning',
  warning: 'icon-warning',
  info: 'icon-info',
  success: 'icon-checkmark',
};

const backgroundClass = computed(() => typeClasses[props.type]);
const iconClass = computed(() => props.icon || iconClasses[props.type]);
</script>

<template>
  <div
    :class="[backgroundClass, 'p-2.5 pr-12 flex gap-2.5 items-start text-white text-sm']"
    role="alert"
  >
    <i :class="['w-6 h-5 bg-white icon shrink-0 mt-0.5', iconClass]"></i>

    <div class="flex-1">
      <!-- Simple message mode -->
      <span v-if="message && !slots.default">{{ message }}</span>

      <!-- Slot content mode (for complex structured content) -->
      <div v-else-if="slots.default">
        <slot></slot>
      </div>

      <button
        v-if="actionButton"
        class="mt-2 text-sm font-medium underline hover:no-underline block"
        @click="actionButton.onClick"
      >
        {{ actionButton.text }}
      </button>
    </div>

    <button v-if="dismissible" class="shrink-0 hover:opacity-70 ml-auto" @click="emit('dismiss')">
      <i class="w-5 h-5 icon icon-close bg-white"></i>
    </button>
  </div>
</template>
